import { useState } from "react";
import { Card, CardContent, CardHeader } from "@tracify/ui/card";
import { Button } from "@tracify/ui/button";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { PlusIcon, LinkSlashIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import {
  useGetFacebookDatasets,
  useDeleteFacebookDataset,
} from "@lib/api-hooks/useFacebookPixel";
import type { FacebookDataset } from "@lib/api-hooks/useFacebookPixel/useGetFacebookPixels";
import { useFeatureFlags } from "@lib/hooks/useFeatureFlags";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { useGetPostbackSourceStatus } from "@lib/api-hooks/useAdAccounts/useGetPostbackSourceStatus";
import { useSetPostbackSourceStatus } from "@lib/api-hooks/useAdAccounts/useSetPostbackSourceStatus";
import CreateFacebookPixelModal from "@components/Dashboards/SettingsDashboard/CreateFacebookPixelModal";
import { DisconnectDatasetModal } from "@components/Dashboards/SettingsDashboard/DisconnectPixelModal";
import { toast } from "sonner";
import AdAccountPostbackControl from "./AdAccountPostbackControl";

export function MetaConversionPostbackCard() {
  const { getFlag } = useFeatureFlags();
  const conversionPostbackEnabled =
    getFlag("conversion_postback_enabled")?.enabled ?? false;

  const { selectedOrganisationData } = useSelectedStores();
  const { data: datasets = [], isLoading } = useGetFacebookDatasets(
    conversionPostbackEnabled ? selectedOrganisationData?.id : undefined
  );
  const { data: postbackStatus, isFetching: isFetchingPostbackStatus } =
    useGetPostbackSourceStatus(selectedOrganisationData?.id, {
      enabled:
        conversionPostbackEnabled && Boolean(selectedOrganisationData?.id),
    });
  const deleteDatasetMutation = useDeleteFacebookDataset();
  const setPostbackSourceStatusMutation = useSetPostbackSourceStatus();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [datasetsToDisconnect, setDatasetsToDisconnect] = useState<
    FacebookDataset[]
  >([]);

  if (!conversionPostbackEnabled) return null;

  const handleDisconnect = async (datasetId?: string) => {
    if (!selectedOrganisationData?.id) return;

    const datasetObj = datasets.find((d) => d.dataset_id === datasetId);
    if (!datasetObj) return;

    try {
      await deleteDatasetMutation.mutateAsync({
        id: datasetObj.id,
        organisationId: selectedOrganisationData.id,
      });
      if (datasets.length <= 1) {
        await setPostbackSourceStatusMutation.mutateAsync({
          source: "facebook",
          organisationId: selectedOrganisationData.id,
          isActiveForPostback: false,
        });
      }
      setDisconnectModalOpen(false);
      setDatasetsToDisconnect([]);
      toast.success("Conversion postback disabled successfully");
    } catch {
      toast.error("Failed to disable conversion postback");
    }
  };

  const isConnected = !isLoading && datasets.length > 0;
  const isSourceActive =
    postbackStatus?.activeForPostback.includes("facebook") ?? false;

  const handleSourceToggle = async (checked: boolean) => {
    if (!selectedOrganisationData?.id) return;

    try {
      await setPostbackSourceStatusMutation.mutateAsync({
        source: "facebook",
        organisationId: selectedOrganisationData.id,
        isActiveForPostback: checked,
      });

      toast.success(
        checked
          ? "Conversion postback enabled successfully"
          : "Conversion postback disabled successfully"
      );
    } catch {
      toast.error(
        `Failed to ${checked ? "activate" : "disable"} conversion postback`
      );
    }
  };

  const handleCreateModalClose = async (success?: boolean) => {
    setCreateModalOpen(false);
    if (!success || !selectedOrganisationData?.id) {
      return;
    }

    try {
      await setPostbackSourceStatusMutation.mutateAsync({
        source: "facebook",
        organisationId: selectedOrganisationData.id,
        isActiveForPostback: true,
      });
    } catch {
      toast.error("Failed to activate conversion postback");
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-muted w-12 h-12 flex items-center justify-center rounded-lg shrink-0">
                <Image
                  src="/static/logos/meta-logo.png"
                  alt="Meta"
                  width={28}
                  height={28}
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </div>
              <p className="h4 text-foreground">Conversion Postback</p>
            </div>
            <div className="flex items-center gap-3">
              {!isLoading &&
                (isConnected ? (
                  <div className="flex items-center gap-1.5">
                    {isSourceActive ? (
                      <CheckCircleIcon className="text-green-500 w-5 h-5" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-foreground border border-dashed" />
                    )}
                    <span className="text-sm">
                      {isSourceActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <div className="h-5 w-5 rounded-full border-foreground border border-dashed" />
                    <span className="text-sm">Not connected</span>
                  </div>
                ))}
              <Button
                variant="primary"
                className="bg-[#5d5fef] hover:bg-[#5d5fef]/90 text-white"
                onClick={() => setCreateModalOpen(true)}
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Connect new dataset
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : isConnected ? (
            <>
              <AdAccountPostbackControl
                checked={isSourceActive}
                disabled={
                  isFetchingPostbackStatus ||
                  setPostbackSourceStatusMutation.isPending
                }
                onCheckedChange={(checked) => {
                  void handleSourceToggle(checked);
                }}
                helperText={
                  isSourceActive
                    ? "Conversion postback is enabled for connected Meta datasets."
                    : "When enabled Tracify will send conversion postback events for connected Meta datasets."
                }
              />
              <div className="space-y-4">
                {datasets.map((ds) => {
                  return (
                    <div key={ds.id} className="rounded-lg p-6 !bg-background">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0 space-y-1 text-sm">
                          <p className="text-muted-foreground">Dataset ID</p>
                          <p className="font-medium break-all">
                            {ds.dataset_id}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Disconnect Meta dataset"
                          className="self-end text-destructive hover:text-destructive hover:bg-destructive/10 sm:self-center"
                          onClick={() => {
                            setDatasetsToDisconnect([ds]);
                            setDisconnectModalOpen(true);
                          }}
                        >
                          <LinkSlashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              No Meta Dataset connected. Use the button above to enable
              conversion postback.
            </p>
          )}
        </CardContent>
      </Card>

      <CreateFacebookPixelModal
        open={createModalOpen}
        onClose={handleCreateModalClose}
      />

      <DisconnectDatasetModal
        isOpen={disconnectModalOpen}
        onClose={() => {
          setDisconnectModalOpen(false);
          setDatasetsToDisconnect([]);
        }}
        onConfirm={handleDisconnect}
        datasets={datasetsToDisconnect}
        isPending={
          deleteDatasetMutation.isPending ||
          setPostbackSourceStatusMutation.isPending
        }
      />
    </>
  );
}
